import { useCallback, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { Route } from '@/routes/_authenticated/setup/labels';
import { Pencil, Plus } from 'lucide-react';
import type { ClassificationLabelResponse } from '@vagusx/clients';
import { P } from '@vagusx/shared';
import { TablePage, SortableHeader } from '@/components/table-page';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { SheetActions, SheetBody, SheetCancel } from '@/components/custom/sheet-layout';
import { LabelBadge } from '@/components/custom/label-badge';
import { DetailRow, DetailSection } from '@/components/custom/detail-section';
import { useLabels } from '@/hooks/use-labels';
import { useHasPermission } from '@/hooks/use-permissions';
import { categoryLabel } from './categories';
import { LabelEditor } from './LabelEditor';

function buildColumns(opts: {
  canEdit: boolean;
  onEdit: (id: string) => void;
}): ColumnDef<ClassificationLabelResponse>[] {
  const cols: ColumnDef<ClassificationLabelResponse>[] = [
    {
      accessorKey: 'displayName',
      header: ({ column }) => <SortableHeader column={column}>Label</SortableHeader>,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <LabelBadge label={row.original} />
          {row.original.isSystem && (
            <Badge variant="outline" className="text-[10px]">
              System
            </Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <span className="line-clamp-1 text-muted-foreground">
          {row.original.description ?? '-'}
        </span>
      ),
    },
    {
      accessorKey: 'enabled',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.enabled ? 'default' : 'secondary'}>
          {row.original.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
      ),
    },
  ];

  if (opts.canEdit) {
    cols.push({
      id: 'actions',
      header: '',
      meta: { headerClassName: 'w-[64px]', cellClassName: 'text-right' },
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          // Hidden until the row is hovered; `focus-visible` keeps it reachable
          // by keyboard. Tailwind gates `group-hover` behind `@media (hover:hover)`,
          // so anything with a touchscreen keeps it permanently visible — including
          // a hybrid laptop, whose *primary* input hovers even when the finger the
          // user actually taps with does not. Without that the button would be
          // invisible but still tappable, swallowing the row tap. `any-pointer`
          // (not `hover`) is the test, because it considers every attached input.
          // No `transition-opacity` here: Button's base `transition-all` already
          // covers opacity, and tailwind-merge would drop it for a narrower class.
          className="h-7 w-7 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 [@media(any-pointer:coarse)]:opacity-100"
          aria-label={`Edit ${row.original.displayName}`}
          onClick={(e) => {
            e.stopPropagation();
            opts.onEdit(row.original.id);
          }}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      ),
    });
  }

  return cols;
}

export default function LabelsPage(): React.ReactElement {
  const { id: selectedLabelId } = Route.useSearch();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const canEdit = useHasPermission(P.CLASSIFICATION_LABELS_EDIT);

  const { data: labels, isLoading, error } = useLabels();
  const selectedLabel = labels?.find((l) => l.id === selectedLabelId) ?? null;

  const handleLabelSelect = useCallback(
    (label: ClassificationLabelResponse) => {
      setIsEditing(false);
      navigate({ to: '/setup/labels', search: { id: label.id }, replace: true });
    },
    [navigate]
  );

  const handleEditFromRow = useCallback(
    (id: string) => {
      setIsEditing(true);
      navigate({ to: '/setup/labels', search: { id }, replace: true });
    },
    [navigate]
  );

  const handleCloseSheet = useCallback(() => {
    setIsEditing(false);
    setIsCreating(false);
    navigate({ to: '/setup/labels', search: {}, replace: true });
  }, [navigate]);

  const columns = useMemo(
    () => buildColumns({ canEdit, onEdit: handleEditFromRow }),
    [canEdit, handleEditFromRow]
  );

  return (
    <TablePage
      title="Classification Labels"
      description="Configure document classification labels for your organization"
      columns={columns}
      data={labels ?? []}
      total={labels?.length || undefined}
      isLoading={isLoading}
      error={error}
      searchColumn="displayName"
      searchPlaceholder="Search labels..."
      onRowClick={handleLabelSelect}
      emptyMessage="No labels found."
      errorMessage="Failed to load labels."
      headerAction={
        canEdit ? (
          <Button onClick={() => setIsCreating(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Label
          </Button>
        ) : undefined
      }
    >
      {/* Create Label Sheet */}
      <Sheet open={isCreating} onOpenChange={(open) => !open && setIsCreating(false)}>
        <SheetContent className="w-[480px] sm:max-w-[480px]">
          <LabelEditor onClose={() => setIsCreating(false)} />
        </SheetContent>
      </Sheet>

      {/* View / Edit Label Sheet */}
      <Sheet
        open={!!selectedLabelId && !isCreating}
        onOpenChange={(open) => !open && handleCloseSheet()}
      >
        <SheetContent className="w-[480px] sm:max-w-[480px]">
          {selectedLabelId && isEditing && (
            <LabelEditor labelId={selectedLabelId} onClose={handleCloseSheet} />
          )}
          {selectedLabelId && !isEditing && selectedLabel && (
            <LabelDetailSheet
              label={selectedLabel}
              canEdit={canEdit}
              onEdit={() => setIsEditing(true)}
            />
          )}
        </SheetContent>
      </Sheet>
    </TablePage>
  );
}

// =============================================================================
// Read-only label detail
// =============================================================================

function LabelDetailSheet({
  label,
  canEdit,
  onEdit,
}: {
  label: ClassificationLabelResponse;
  canEdit: boolean;
  onEdit: () => void;
}): React.ReactElement {
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <LabelBadge label={label} />
          {label.isSystem && (
            <Badge variant="outline" className="text-[10px]">
              System
            </Badge>
          )}
        </SheetTitle>
        <SheetDescription>Label details</SheetDescription>
      </SheetHeader>

      <SheetBody>
        <div className="space-y-2">
          <DetailSection title="General">
            <DetailRow label="Name" value={label.displayName} />
            <DetailRow label="Slug" value={label.name} />
            <DetailRow label="Category" value={categoryLabel(label.labelCategory)} />
            <DetailRow label="Status" value={label.enabled ? 'Enabled' : 'Disabled'} />
          </DetailSection>

          {label.description && (
            <DetailSection title="Description">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {label.description}
              </p>
            </DetailSection>
          )}

          {label.classificationPrompt && (
            <DetailSection title="Classification Prompt">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {label.classificationPrompt}
              </p>
            </DetailSection>
          )}
        </div>
      </SheetBody>

      {canEdit && (
        <SheetActions>
          <SheetCancel />
          <Button onClick={onEdit}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" />
            Edit
          </Button>
        </SheetActions>
      )}
    </>
  );
}
